import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import StructuredData from '../../components/feature/StructuredData';
import ScrollToTop from '../../components/feature/ScrollToTop';

const ServiciosPage = () => {
  useEffect(() => {
    document.title = 'Servicios de Vaciado Barcelona - BIOTRAUMA';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Servicios de vaciado de pisos, locales, trasteros y retirada de muebles en Barcelona. Limpieza final y gestión de residuos. Presupuesto sin compromiso.'
      );
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Servicios de Vaciado Barcelona - BIOTRAUMA');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = 'Servicios de Vaciado Barcelona - BIOTRAUMA';
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute(
        'content',
        'Servicios de vaciado de pisos, locales, trasteros y retirada de muebles en Barcelona. Limpieza final y gestión de residuos. Presupuesto sin compromiso.'
      );
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = 'Servicios de vaciado de pisos, locales, trasteros y retirada de muebles en Barcelona. Limpieza final y gestión de residuos. Presupuesto sin compromiso.';
      document.head.appendChild(meta);
    }

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://barcelona.biotrauma.es/servicios');
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:url');
      meta.content = 'https://barcelona.biotrauma.es/servicios';
      document.head.appendChild(meta);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://barcelona.biotrauma.es/servicios');
    }
  }, []);

  const services = [
    {
      title: 'Vaciado de pisos y casas',
      slug: 'vaciado-pisos-casas',
      icon: 'ri-home-4-line',
      image: 'https://public.readdy.ai/ai/img_res/0062f9f96cb40b87f5883148b7cf84dc.jpg',
      description: (
        <>
          <a href="https://guia.barcelona.cat/es/detall/punt-verd-de-barri-horta-guinardo_99400038990.html" target="_blank" rel="noopener noreferrer" className="text-orange hover:underline cursor-pointer">Servicio completo de vaciado de viviendas en Barcelona</a> y área metropolitana. Retiramos todos los muebles, electrodomésticos y enseres, dejando el inmueble completamente vacío.
        </>
      ),
      includes: [
        'Retirada de todo tipo de muebles y enseres',
        'Desmontaje básico de armarios y estanterías',
        'Carga, transporte y descarga',
        'Protección de zonas comunes',
        'Gestión de residuos en puntos autorizados',
        'Coordinación con portería y administración',
      ],
      ideal: 'Ideal para herencias, mudanzas, reformas integrales o preparación para venta/alquiler.',
      tiempo: 'Un piso estándar de 70-80m² se vacía en 4-6 horas. Pisos más grandes pueden requerir 1-2 días.',
      noIncluye: 'No incluye limpieza profunda ni obras. Servicio mínimo: una furgoneta completa.',
    },
    {
      title: 'Vaciado de locales comerciales',
      slug: 'vaciado-locales',
      icon: 'ri-store-2-line',
      image: 'https://readdy.ai/api/search-image?query=Empty%20commercial%20space%20interior%20Barcelona%20clean%20floors%20white%20walls%20large%20windows&width=1200&height=900&seq=service-locales-003&orientation=landscape',
      description: 'Vaciado profesional de locales comerciales, oficinas y espacios de negocio. Retiramos mobiliario comercial, estanterías, mostradores y todo el equipamiento.',
      includes: [
        'Retirada de mobiliario comercial',
        'Desmontaje de estanterías y mostradores',
        'Retirada de equipamiento y maquinaria ligera',
        'Gestión de residuos comerciales',
        'Trabajo fuera de horario comercial si es necesario',
        'Certificado de gestión de residuos',
      ],
      ideal: 'Perfecto para cierre de negocios, cambio de actividad o traspaso de local.',
      tiempo: 'Según tamaño del local. Un local de 100m² se vacía en 1-2 días laborables.',
      noIncluye: 'No incluye maquinaria pesada ni instalaciones fijas. Consultar para equipos especiales.',
    },
    {
      title: 'Vaciado de trasteros y garajes',
      slug: 'vaciado-trasteros',
      icon: 'ri-archive-line',
      image: 'https://readdy.ai/api/search-image?query=Empty%20clean%20storage%20room%20garage%20space%20Barcelona%20concrete%20walls%20good%20lighting&width=1200&height=900&seq=service-trasteros-004&orientation=landscape',
      description: 'Limpieza y vaciado completo de trasteros, garajes y espacios de almacenamiento. Retiramos objetos acumulados, cajas, muebles viejos y todo tipo de enseres.',
      includes: [
        'Retirada de todo el contenido',
        'Clasificación básica de objetos',
        'Carga y transporte',
        'Barrido final del espacio',
        'Gestión de residuos',
        'Trabajo en sótanos y plantas bajas',
      ],
      ideal: 'Ideal para liberar espacio, preparar venta de plaza de garaje o limpieza de herencias.',
      tiempo: 'Un trastero estándar se vacía en 2-4 horas según volumen acumulado.',
      noIncluye: 'Servicio mínimo: media furgoneta. No retiramos residuos peligrosos ni químicos.',
    },
    {
      title: 'Retirada de muebles y electrodomésticos',
      slug: 'retirada-muebles',
      icon: 'ri-truck-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20workers%20loading%20furniture%20into%20moving%20truck%20Barcelona%20street&width=1200&height=900&seq=service-muebles-005&orientation=landscape',
      description: 'Servicio de recogida y transporte de muebles y electrodomésticos. Retiramos sofás, armarios, camas, neveras, lavadoras y todo tipo de mobiliario.',
      includes: [
        'Recogida en el domicilio',
        'Desmontaje si es necesario',
        'Bajada por escaleras o ascensor',
        'Carga y transporte',
        'Gestión en punto limpio autorizado',
        'Protección de zonas comunes',
      ],
      ideal: 'Perfecto para renovación de mobiliario o retirada de electrodomésticos averiados.',
      tiempo: 'Servicio express disponible. Recogida en 24-48 horas según disponibilidad.',
      noIncluye: 'Servicio mínimo: una furgoneta completa. No recogemos objetos sueltos pequeños.',
    },
    {
      title: 'Limpieza final y puesta a punto',
      slug: 'limpieza-final',
      icon: 'ri-brush-line',
      image: 'https://readdy.ai/api/search-image?query=Clean%20spotless%20empty%20apartment%20interior%20Barcelona%20shining%20floors%20white%20walls%20sparkling%20windows&width=1200&height=900&seq=service-limpieza-006&orientation=landscape',
      description: 'Servicio de limpieza profunda tras el vaciado. Dejamos el inmueble impecable y listo para entregar, vender o alquilar.',
      includes: [
        'Limpieza profunda de todas las estancias',
        'Limpieza de ventanas y cristales',
        'Barrido y fregado de suelos',
        'Limpieza de baños y cocina',
        'Retirada de polvo y suciedad',
        'Revisión final del inmueble',
      ],
      ideal: 'Complemento perfecto tras un vaciado para entregar el inmueble en condiciones óptimas.',
      tiempo: 'Un piso de 80m² se limpia en 3-4 horas. Servicio opcional tras vaciado.',
      noIncluye: 'No incluye limpieza de obras ni reparaciones. Solo limpieza de mantenimiento.',
    },
    {
      title: 'Coordinación de pintura y reparaciones',
      slug: 'pintura-reparaciones',
      icon: 'ri-tools-line',
      image: 'https://readdy.ai/api/search-image?query=Professional%20painter%20working%20in%20empty%20Barcelona%20apartment%20with%20painting%20tools%20freshly%20painted%20white%20walls&width=1200&height=900&seq=service-pintura-007&orientation=landscape',
      description: 'Coordinamos servicios de pintura y pequeñas reparaciones para dejar tu inmueble completamente renovado y listo para su nuevo uso.',
      includes: [
        'Coordinación con profesionales de confianza',
        'Pintura de paredes y techos',
        'Reparaciones menores de albañilería',
        'Arreglo de desperfectos básicos',
        'Supervisión del trabajo',
        'Entrega llave en mano',
      ],
      ideal: 'Para propietarios que quieren entregar el inmueble completamente renovado.',
      tiempo: 'Según alcance de trabajos. Pintura completa de piso 80m²: 2-3 días.',
      noIncluye: 'Servicio opcional bajo presupuesto separado. No incluye reformas integrales.',
    },
  ];

  return (
    <>
      <StructuredData type="servicios" />
      <Navbar />
      <main>
        <section className="relative h-[40vh] min-h-[400px] flex items-center overflow-hidden pt-20 md:pt-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                'url(https://readdy.ai/api/search-image?query=Professional%20house%20clearance%20team%20working%20with%20moving%20boxes%20furniture%20Barcelona&width=1920&height=800&seq=servicios-hero-bg-002&orientation=landscape)',
            }}
          />
          <div className="absolute inset-0 bg-navy/75" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center py-8">
            <nav className="text-white/80 text-sm mb-4">
              <a href="/" className="hover:text-white cursor-pointer">Inicio</a>
              <span className="mx-2">/</span>
              <span className="text-white">Servicios</span>
            </nav>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-6xl font-bold text-white mb-5"
            >
              Nuestros servicios
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-white/90 max-w-2xl mx-auto"
            >
              Soluciones completas de vaciado y retirada de enseres en Barcelona. Conoce <a href="/empresa" className="text-orange hover:underline font-medium">nuestra empresa</a> y nuestros valores.
            </motion.p>
          </div>
        </section>

        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {services.map((service, index) => (
              <div
                key={index}
                id={service.slug}
                className={`mb-20 pb-20 ${
                  index !== services.length - 1 ? 'border-b border-gray-200' : ''
                }`}
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className={index % 2 === 1 ? 'lg:order-2' : ''}
                  >
                    <span className="inline-block bg-orange-bg text-orange text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
                      Servicio {index + 1}
                    </span>
                    <h2 className="text-4xl font-bold text-navy mb-5">{service.title}</h2>
                    <div className="text-lg text-gray-700 leading-relaxed mb-6">
                      {service.description}
                    </div>

                    <div className="mb-6">
                      <h3 className="text-lg font-bold text-navy mb-3">¿Qué incluye?</h3>
                      <ul className="space-y-2">
                        {service.includes.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange text-xl flex-shrink-0 mt-0.5"></i>
                            <span className="text-sm text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-5 mb-6 space-y-3">
                      <div>
                        <strong className="text-navy">¿Cuándo conviene?</strong>
                        <p className="text-sm text-gray-700 mt-1">{service.ideal}</p>
                      </div>
                      <div>
                        <strong className="text-navy">Tiempo estimado:</strong>
                        <p className="text-sm text-gray-700 mt-1">{service.tiempo}</p>
                      </div>
                      <div>
                        <strong className="text-navy">Importante:</strong>
                        <p className="text-sm text-gray-700 mt-1">{service.noIncluye}</p>
                      </div>
                      {index === 0 && (
                        <div>
                          <p className="text-sm text-gray-700 mt-1">
                            ¿Dudas sobre el proceso? Consulta <a href="/faq" className="text-orange hover:underline font-medium">nuestras preguntas frecuentes</a>.
                          </p>
                        </div>
                      )}
                    </div>

                    <a
                      href="https://wa.me/34629501941"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center bg-orange text-white px-8 py-4 rounded-full font-medium hover:bg-orange-light hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-whatsapp-line text-xl mr-2"></i>
                      Solicitar este servicio
                    </a>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    className={index % 2 === 1 ? 'lg:order-1' : ''}
                  >
                    <div className="rounded-2xl overflow-hidden shadow-xl aspect-[4/3]">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gray-50 py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-navy mb-5">
              ¿No encuentras lo que buscas?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Cada situación es única. Cuéntanos qué necesitas y te prepararemos un presupuesto personalizado. Conoce más sobre <a href="/empresa" className="text-orange hover:underline font-medium">cómo trabajamos</a>.
            </p>
            
            <div className="inline-flex items-center gap-3 bg-orange/10 border-2 border-orange text-orange px-6 py-3 rounded-full mb-10">
              <i className="ri-information-line text-xl"></i>
              <span className="font-bold text-sm">NO RECOGEMOS MUEBLES SUELTOS</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-orange text-white text-base font-medium px-8 py-4 rounded-full hover:bg-orange-light hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl mr-2"></i>
                Consultar por WhatsApp
              </a>
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center border-2 border-navy bg-transparent text-navy text-base font-medium px-8 py-4 rounded-full hover:bg-navy hover:text-white transition-all whitespace-nowrap cursor-pointer"
              >
                Formulario de contacto
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
      <ScrollToTop />
    </>
  );
};

export default ServiciosPage;
